import type { ClientLifecycle } from "../client/handle-errors.ts";
import type { PreferencesStorage } from "./preferences-storage.ts";
import { type Actor, type InvalidationSink, LayoutChanged } from "../invalidation.ts";
import { StoredPreference } from "./stored-preference.ts";
import type { Timescale } from "@tento-lona/sheets";

const DATE_UNITS: readonly Timescale[] = ["year", "month", "week", "day"];

// Narrower-than-normal counts for the docked sidebar layout. The
// normal per-sheet defaults are day=7, week/month/year=3 (SheetViewPreferences);
// docking shows fewer cells because the content area is narrower.
const DEFAULT_DOCKED_COUNTS: Map<Timescale, number> = new Map([
  ["year", 2],
  ["month", 2],
  ["week", 2],
  ["day", 3],
]);

const STORAGE_KEY = "lona-pref-docked-column-counts";

/**
 * Per-timescale calendar column counts used while the sidebar is docked
 * (`client.preferences.dockedColumnCount()`): how many cells a docked view
 * shows. Global (one set of counts for all sheets), stored locally,
 * maintained separately from the
 * per-sheet `SheetViewPreferences`: docking drives the view from this config
 * and never overwrites the user's saved per-sheet counts.
 */
export class DockedColumnCountPreferences {
  readonly #storage: PreferencesStorage;
  readonly #invalidation: InvalidationSink;
  readonly #lifecycle: ClientLifecycle;

  /** `lifecycle`: the owning client's (a standalone face: `new LonaSdk.Client.Lifecycle()`). */
  constructor(storage: PreferencesStorage, invalidation: InvalidationSink, lifecycle: ClientLifecycle) {
    this.#storage = storage;
    this.#invalidation = invalidation;
    this.#lifecycle = lifecycle;
  }

  static defaultCounts(): Map<Timescale, number> {
    return new Map(DEFAULT_DOCKED_COUNTS);
  }

  #read(): Map<Timescale, number> {
    const counts = new Map(DEFAULT_DOCKED_COUNTS);
    // Unusable storage is logged and falls back to the defaults.
    const raw = StoredPreference.record(
      STORAGE_KEY,
      StoredPreference.json(this.#storage, STORAGE_KEY),
    );
    for (const [scale, value] of Object.entries(raw ?? {})) {
      const unit = StoredPreference.oneOf(STORAGE_KEY, scale, DATE_UNITS);
      const count = StoredPreference.finite(STORAGE_KEY, value);
      if (unit != null && count != null) counts.set(unit, count);
    }
    return counts;
  }

  /** The column count to show at `timeScale` while docked (the default when none is stored). */
  peek(timescale: Timescale): number {
    return this.#read().get(timescale) ?? DEFAULT_DOCKED_COUNTS.get(timescale) ?? 3;
  }

  /** Store `count` columns for `timeScale` and announce `LayoutChanged` stamped with `actor`, the writer. */
  set(timescale: Timescale, count: number, actor: Actor): void {
    this.#lifecycle.assertLive("preferences.dockedColumnCount.set");
    const counts = this.#read();
    counts.set(timescale, count);
    this.#storage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(counts)));
    this.#invalidation.emit(new LayoutChanged(actor));
  }
}
